import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_CONFIG } from "@/config/api";
import { validateRedirectPath } from "@/config/auth";
import { Button } from "@/components/ui/Button";
import { TabGroup, type TabItem } from "@/components/ui/TabGroup";
import { listRefreshablePortalSessions } from "@/core/auth/listPortalSessions";
import { useAppStore } from "@/core/store/useAppStore";
import { AdminSignInAction } from "./components/AdminSignInAction";
import { LoginPage } from "./components/LoginPage";
import { LoginCodeForm } from "./LoginCodeForm";
import { isStandaloneDisplayMode } from "./loginCode";
import { EMAIL_FIELD, LOGIN_BUTTON, LOGIN_ERROR, LOGIN_HEADING, LOGIN_INPUT, LOGIN_LABEL, LOGIN_PRIMARY, LOGIN_TAB, LOGIN_TEXT } from "./loginStyles";

/**
 * Self-serve magic link request page.
 *
 * Flow:
 *  1. User enters an email and submits.
 *  2. We POST `/portal/auth/request-magic-link` with `{email, redirect?}`;
 *     the backend always responds 200 to avoid leaking which addresses
 *     are registered.
 *  3. We flip to a "check your inbox" state with a 30s resend cooldown.
 *
 * The exchange page at `/collaborators/:clientId/auth?token=...` remains
 * the entry point that accepts the token from the email and honours any
 * `?redirect=` carried through.
 *
 * Deep-linking: when arriving at `/portal/login?redirect=/client/<id>/...`
 * we (a) try to short-circuit the form if a live session already exists
 * for the target client, and (b) thread the redirect into the magic-link
 * email so the final landing page is the requested resource.
 */

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESEND_COOLDOWN_SECONDS = 30;

type Stage = "enter-email" | "sent";

/**
 * One login for every portal role (DD-026). The backend resolves role per
 * email row, then the flow funnels role -> client; this page never needs
 * to know which role the email holds. Admins sign in from the same page
 * through the secondary Google action (spec 2026-10-15).
 */
type LoginMode = "link" | "code";

/** The client's two ways in, as the tabs at the top of the card, each as tall as every other login control. */
const LOGIN_MODES: TabItem<LoginMode>[] = [
  { id: "link", label: "Ricevi un link", className: LOGIN_TAB },
  { id: "code", label: "Ricevi un codice", className: LOGIN_TAB },
];

const LOGIN_COPY = {
  heading: "Accedi al portale",
  subhead: "Inserisci la tua email e ti invieremo un link di accesso.",
};

/**
 * Extract the clientId from a validated redirect path. Both `/client/<id>/...`
 * and `/creator/<id>` share the "second segment is clientId" shape.
 * Returns null when the path does not encode a client.
 */
function extractClientId(redirectPath: string | null): string | null {
  if (!redirectPath) return null;
  const base = redirectPath.split("?")[0];
  const segments = base.split("/").filter(Boolean);
  if (segments.length < 2) return null;
  const [head, id] = segments;
  if (head !== "client" && head !== "creator") return null;
  return id || null;
}

export const ClientMagicLoginRoute: React.FC = () => {
  const copy = LOGIN_COPY;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const rawRedirect = searchParams.get("redirect");
  const validatedRedirect = useMemo(
    () => validateRedirectPath(rawRedirect),
    [rawRedirect]
  );
  const redirectClientId = useMemo(
    () => extractClientId(validatedRedirect),
    [validatedRedirect]
  );

  const [mode, setMode] = useState<LoginMode>(
    isStandaloneDisplayMode() ? "code" : "link"
  );
  const [stage, setStage] = useState<Stage>("enter-email");
  const [email, setEmail] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [networkError, setNetworkError] = useState<string | null>(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const cooldownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const clearSignedOutByUser = useAppStore((state) => state.clearSignedOutByUser);

  // An explicit sign-out only has to land here (spec 2026-10-15). Clear its
  // flag, so a client who then signs in from this tab routes into the portal.
  useEffect(() => {
    clearSignedOutByUser();
  }, [clearSignedOutByUser]);

  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    };
  }, []);

  // Short-circuit: if a valid session already exists for the target client
  // (carried via `?redirect=/client/<id>/...`), skip the form entirely.
  useEffect(() => {
    if (!validatedRedirect || !redirectClientId) return;
    const sessions = listRefreshablePortalSessions();
    if (sessions.some((s) => s.clientId === redirectClientId)) {
      navigate(validatedRedirect, { replace: true });
    }
  }, [validatedRedirect, redirectClientId, navigate]);

  const startCooldown = () => {
    setCooldownRemaining(RESEND_COOLDOWN_SECONDS);
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
    }
    cooldownTimerRef.current = setInterval(() => {
      setCooldownRemaining((prev) => {
        if (prev <= 1) {
          if (cooldownTimerRef.current) {
            clearInterval(cooldownTimerRef.current);
            cooldownTimerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const requestMagicLink = async (targetEmail: string): Promise<boolean> => {
    try {
      const body: { email: string; redirect?: string } = { email: targetEmail };
      if (validatedRedirect) body.redirect = validatedRedirect;

      const response = await fetch(
        `${API_CONFIG.URL}/portal/auth/request-magic-link`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      return true;
    } catch (err) {
      console.error("Magic link request failed:", err);
      setNetworkError(
        err instanceof Error
          ? err.message
          : "Impossibile contattare il server. Riprova."
      );
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNetworkError(null);

    const normalized = email.trim().toLowerCase();
    if (!EMAIL_REGEX.test(normalized)) {
      setNetworkError("Inserisci un indirizzo email valido.");
      return;
    }

    setIsSubmitting(true);
    const ok = await requestMagicLink(normalized);
    setIsSubmitting(false);

    if (ok) {
      setSubmittedEmail(normalized);
      setStage("sent");
      startCooldown();
    }
  };

  const handleResend = async () => {
    if (cooldownRemaining > 0 || isSubmitting || !submittedEmail) return;
    setNetworkError(null);
    setIsSubmitting(true);
    const ok = await requestMagicLink(submittedEmail);
    setIsSubmitting(false);
    if (ok) {
      startCooldown();
    }
  };

  const handleUseDifferentEmail = () => {
    setStage("enter-email");
    setNetworkError(null);
    setCooldownRemaining(0);
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
      cooldownTimerRef.current = null;
    }
  };

  return (
    // The team's way in is not one of the client's, so it sits apart from the card.
    <LoginPage footer={<AdminSignInAction redirect={rawRedirect} />}>
      {/* How the client gets in, chosen before the email. DD-018: the installed app is code-only, since an
          emailed link would open in the browser's storage partition and leave the app logged out. */}
      {!isStandaloneDisplayMode() && (
        <TabGroup<LoginMode>
          items={LOGIN_MODES}
          activeId={mode}
          onTabChange={setMode}
          config={{ variant: "navigation", size: "lg", layout: "horizontal", fullWidth: true, hasBackground: true }}
          className="mb-(--login-gap)"
        />
      )}
      {mode === "code" ? (
        <>
          <div className="mb-(--login-gap)">
            <h1 className={LOGIN_HEADING}>{copy.heading}</h1>
            <p className={LOGIN_TEXT}>
              Inserisci la tua email e ti invieremo un codice di accesso.
            </p>
          </div>
          <LoginCodeForm />
        </>
      ) : (
        <>
          {stage === "enter-email" ? (
            <>
              <div className="mb-(--login-gap)">
                <h1 className={LOGIN_HEADING}>{copy.heading}</h1>
                <p className={LOGIN_TEXT}>{copy.subhead}</p>
              </div>

              {networkError && <p className={`mb-[24px] ${LOGIN_ERROR}`}>{networkError}</p>}

              <form onSubmit={handleSubmit} className="space-y-[24px]">
                <div>
                  <label htmlFor="login-email" className={LOGIN_LABEL}>
                    Email
                  </label>
                  <input
                    id="login-email"
                    {...EMAIL_FIELD}
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@esempio.com"
                    className={LOGIN_INPUT}
                    disabled={isSubmitting}
                    required
                  />
                </div>
                {/* Never greyed out while the field is empty: a faded button reads as broken. Submit validates. */}
                <Button type="submit" variant="primary" size="lg" isLoading={isSubmitting} className={LOGIN_PRIMARY}>
                  {isSubmitting ? "Invio in corso..." : "Invia link di accesso"}
                </Button>
              </form>
            </>
          ) : (
            <>
              <div className="mb-(--login-gap)">
                <h1 className={LOGIN_HEADING}>Controlla la tua casella</h1>
                <p className={LOGIN_TEXT}>
                  Se <span className="font-medium text-gray-900">{submittedEmail}</span>{" "}
                  è associata a un account BeNerdy, ti abbiamo inviato un link di accesso.
                </p>
              </div>

              <div className={`p-[16px] bg-white/70 rounded-xl mb-(--login-gap) ${LOGIN_TEXT}`}>
                Il link scade dopo poco tempo. Aprilo da qualsiasi dispositivo per
                completare l'accesso.
              </div>

              {networkError && <p className={`mb-[24px] ${LOGIN_ERROR}`}>{networkError}</p>}

              <p className={`mb-[12px] ${LOGIN_TEXT}`}>Non l'hai ricevuta?</p>
              <div className="space-y-[16px]">
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  className={LOGIN_BUTTON}
                  onClick={handleResend}
                  disabled={cooldownRemaining > 0 || isSubmitting}
                >
                  {isSubmitting
                    ? "Invio in corso..."
                    : cooldownRemaining > 0
                      ? `Reinvia tra ${cooldownRemaining}s`
                      : "Reinvia"}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  className={LOGIN_BUTTON}
                  onClick={handleUseDifferentEmail}
                >
                  Usa un'altra email
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </LoginPage>
  );
};
